import { FC, FormEvent, Fragment, useMemo, useState, useEffect } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
    Autocomplete,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    TextField,
} from '@mui/material';
import { createFilterOptions, SxProps } from '@mui/material';
import { DealerGroup } from '../../../types/DealerGroup';
import ApiService from '../../../ApiService';

interface DealerGroupAutocompleteProps {
    agencyId: number;
    value: number | number[] | null;
    onChange: (value: DealerGroup | DealerGroup[] | null) => void;
    multiple?: boolean;
    sx?: SxProps;
    label?: string;
}

const filter = createFilterOptions<DealerGroup>();

const parseInputValue = (inputValue: string): string => {
    const match = inputValue.match(/"([^"]+)"/);
    return match ? match[1] : '';
};

const DealerGroupAutocomplete: FC<DealerGroupAutocompleteProps> = ({
    agencyId,
    value,
    onChange,
    label = 'Select Dealer Group',
    multiple = false,
    sx = {},
}) => {
    const [open, toggleOpen] = useState<boolean>(false);
    const [dialogValue, setDialogValue] = useState<Partial<DealerGroup>>({
        agencyId: agencyId,
        name: '',
    });

    const { data } = useQuery({
        queryKey: ['dealerGroups'],
        queryFn: async () => {
            const { data } = await ApiService.getDealerGroups();
            return data;
        },
        enabled: true,
    });

    const [options, setOptions] = useState<DealerGroup[]>([]);

    useEffect(() => {
        setOptions(data || []);
    }, [data]);

    const selectedOptions = useMemo(() => {
        return Array.isArray(value)
            ? options?.filter((dealerGroup) => value.includes(dealerGroup.id)) || []
            : options?.find((dealerGroup) => dealerGroup.id === value)
            ? options?.find((dealerGroup) => dealerGroup.id === value)
            : null;
    }, [options, value]);

    useEffect(() => {}, [selectedOptions]);

    const handleClose = (): void => {
        setDialogValue({
            ...dialogValue,
            agencyId: agencyId,
            name: '',
        });
        toggleOpen(false);
    };

    return (
        <Fragment>
            <Autocomplete
                freeSolo
                selectOnFocus
                clearOnBlur
                handleHomeEndKeys
                size="small"
                multiple={multiple}
                value={selectedOptions as any}
                onChange={(_, newValue) => {
                    if (typeof newValue === 'string') {
                        setTimeout(() => {
                            toggleOpen(true);
                            setDialogValue((prev) => ({
                                ...prev,
                                name: newValue,
                            }));
                        });
                    } else if (newValue && 'id' in newValue && (!newValue?.id || newValue.id === -1)) {
                        toggleOpen(true);
                        setDialogValue((prev) => ({
                            ...prev,
                            name: newValue.name,
                        }));
                    } else {
                        onChange(newValue as any);
                    }
                }}
                options={options}
                sx={sx}
                filterOptions={(options, params) => {
                    const filtered = filter(options, params);

                    if (params.inputValue !== '') {
                        filtered.push({
                            id: -1, // Temporary ID or handle it dynamically
                            name: `Add "${params.inputValue}"`,
                            agencyId: 1, // Add appropriate agencyId or handle it
                        });
                    }

                    return filtered;
                }}
                getOptionLabel={(option: DealerGroup | string) => {
                    if (typeof option === 'string') {
                        return option as string;
                    }
                    if ('name' in option) {
                        return option.name;
                    }
                    // if (option.inputValue) {
                    //     return option.inputValue;
                    // }
                    return '';
                }}
                isOptionEqualToValue={(option: DealerGroup, value: DealerGroup) => option.id === value.id}
                renderInput={(params) => <TextField {...params} label={label} />}
            />

            <DealerGroupCreateFormDialog
                open={open}
                onClose={handleClose}
                data={dialogValue}
                onCreate={(newDealerGroup: DealerGroup) => {
                    setOptions((prev) => [...prev, newDealerGroup]);

                    onChange(newDealerGroup);
                }}
            />

            {/*<Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
                <form onSubmit={handleSubmit}>
                    <DialogTitle>Add a new Dealer Group</DialogTitle>
                    <Divider />
                    <DialogContent>
                        <TextField
                            autoFocus
                            margin="dense"
                            id="name"
                            value={dialogValue.name}
                            onChange={(event) =>
                                setDialogValue({
                                    ...dialogValue,
                                    name: event.target.value,
                                })
                            }
                            label="Dealer Group Name"
                            type="text"
                            variant="outlined"
                        />
                    </DialogContent>
                    <Divider />
                    <DialogActions>
                        <Button variant="outlined" onClick={handleClose}>Cancel</Button>
                        <Button variant="outlined" color="primary" type="submit">Add Dealer Group</Button>
                    </DialogActions>
                </form>
            </Dialog>*/}
        </Fragment>
    );
};

interface DealerGroupCreateFormDialogProps {
    data: Partial<DealerGroup>;
    open: boolean;
    onClose: () => void;
    onCreate?: (newDealerGroup: DealerGroup) => void;
}

const DealerGroupCreateFormDialog: FC<DealerGroupCreateFormDialogProps> = ({ open, onClose, onCreate, ...props }) => {
    const [data, setData] = useState<Partial<DealerGroup>>(props.data);

    useEffect(() => {
        setData({
            ...props.data,
            name: parseInputValue(props.data.name as string),
        });
    }, [props.data]);

    const mutation = useMutation({
        mutationFn: async (data: Partial<DealerGroup>) => {
            const response = await ApiService.createDealerGroup(data);
            return response.data;
        },
        onSuccess: (newDealerGroup) => {
            alert('Dealer Group added successfully!');
            handleClose();

            if (onCreate) {
                onCreate(newDealerGroup);
            }
        },
        onError: (error) => {
            console.error('Error adding Dealer Group:', error);
        },
    });

    const handleClose = (): void => {
        setData({
            name: '',
        });
        onClose();
    };

    const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        event.stopPropagation();

        mutation.mutate(data);
    };

    return (
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
            <form onSubmit={handleSubmit}>
                <DialogTitle>Add a new Dealer Group</DialogTitle>
                <Divider />
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        id="name"
                        value={data.name}
                        onChange={(event) => {
                            setData({
                                ...data,
                                name: event.target.value,
                            });
                        }}
                        label="Dealer Group Name"
                        type="text"
                        variant="outlined"
                    />
                </DialogContent>
                <Divider />
                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button variant="outlined" onClick={handleClose}>
                        Cancel
                    </Button>
                    <Button variant="outlined" color="primary" type="submit">
                        Add Dealer Group
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};

export default DealerGroupAutocomplete;
